import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import {
  Wallet,
  SlidersHorizontal,
  RefreshCw,
  TrendingUp,
} from "lucide-react";
import { Button, Pagination } from "@/components/ui";
import {
  ListResultsCard,
  TransactionItem,
} from "@/components/common";
import { EmptyState } from "@/components/ui";
import { DeferredFilterActions } from "@/components/common/DeferredFilterPanel";
import { WalletTransactionFilterFields } from "@/components/common/WalletTransactionFilterFields";
import { useDeferredFilters } from "@/hooks/useDeferredFilters";
import {
  buildWalletTransactionFilterParams,
  countNonDateWalletFilters,
  filterWalletTransactionsClientSide,
  getDefaultWalletTransactionFilters,
} from "@/lib/walletTransactionFilters";
import { formatMoney } from "@/lib/money";
import type { WalletTransaction } from "@/types";

const ITEMS_PER_PAGE = 10;

interface WalletStats {
  total_credits: number;
  total_debits: number;
  transactions_count: number;
}

interface ActivityFilterOption {
  id: string;
  name: string;
}

interface TransactionsSectionProps {
  /**
   * Unique ID for query caching (e.g., partner ID or host ID)
   */
  entityId: string;
  /**
   * Type of entity (for query key naming)
   */
  entityType: "seller" | "host" | "sales-agent";
  /**
   * Function to fetch transactions
   */
  fetchTransactions: (params: {
    skip?: number;
    limit?: number;
    start_date?: string;
    end_date?: string;
    activity_id?: string;
  }) => Promise<{ items: WalletTransaction[]; total: number }>;
  /**
   * Function to fetch wallet stats
   */
  fetchStats: (params: {
    start_date?: string;
    end_date?: string;
    activity_id?: string;
  }) => Promise<WalletStats>;
  /**
   * Optional list of activities for the activity filter (only for partners)
   */
  activities?: ActivityFilterOption[];
  /**
   * Title for the card
   */
  title?: string;
}

export function TransactionsSection({
  entityId,
  entityType,
  fetchTransactions,
  fetchStats,
  activities,
  title,
}: TransactionsSectionProps) {
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const { t } = useTranslation("components");
  const resolvedTitle = title ?? t("transactionsSection.titleDefault");
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const {
    formFilters,
    appliedFilters,
    updateFormField,
    mergeFormFields,
    applyFilters,
    resetFilters,
    hasUnappliedChanges,
  } = useDeferredFilters(getDefaultWalletTransactionFilters, {
    onApply: () => setCurrentPage(1),
    onReset: () => setCurrentPage(1),
  });

  const hasActiveFilters = useMemo(
    () => countNonDateWalletFilters(appliedFilters) > 0,
    [appliedFilters],
  );

  const filterParams = useMemo(
    () => buildWalletTransactionFilterParams(appliedFilters),
    [appliedFilters],
  );

  const { data: walletStats } = useQuery({
    queryKey: [entityType, entityId, "wallet-stats", filterParams],
    queryFn: () => fetchStats(filterParams),
    enabled: !!entityId,
  });

  const {
    data: transactionsData,
    isLoading: isLoadingTransactions,
    refetch: refetchTransactions,
    isFetching: isFetchingTransactions,
  } = useQuery({
    queryKey: [entityType, entityId, "transactions", filterParams, currentPage],
    queryFn: () =>
      fetchTransactions({
        ...filterParams,
        skip: (currentPage - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE * 10,
      }),
    enabled: !!entityId,
  });

  const filteredTransactions = useMemo(() => {
    if (!transactionsData?.items) return [];
    return filterWalletTransactionsClientSide(
      transactionsData.items,
      appliedFilters,
      t,
    );
  }, [transactionsData, appliedFilters, t]);

  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredTransactions.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTransactions, currentPage]);

  const totalPages = Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE);

  const showActivityFilter =
    entityType === "seller" && activities && activities.length > 0;

  return (
    <ListResultsCard
      title={
        <span className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          {resolvedTitle}
        </span>
      }
      headerAction={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <SlidersHorizontal className="mr-1 h-4 w-4" />
            {t("transactionsSection.filters")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            isLoading={isFetchingTransactions}
            leftIcon={<RefreshCw className="h-4 w-4" />}
            onClick={() => refetchTransactions()}
          />
        </div>
      }
      beforeContent={
        <>
          {showFilters ? (
            <div className="border-t border-surface-200 bg-surface-50 p-5">
              <WalletTransactionFilterFields
                filters={formFilters}
                labels={{
                  dateRange: t("transactionsSection.dateRange"),
                  searchDescriptionLabel: t(
                    "transactionsSection.searchInDescription",
                  ),
                  searchDescriptionPlaceholder: t(
                    "transactionsSection.searchPlaceholder",
                  ),
                  activityLabel: t("transactionsSection.activity"),
                  allActivities: t("transactionsSection.allActivities"),
                  minAmount: t("transactionsSection.minAmountEuro"),
                  maxAmount: t("transactionsSection.maxAmountEuro"),
                  placeholderMin: t("transactionsSection.minAmountPlaceholder"),
                  placeholderMax: t("transactionsSection.noLimit"),
                }}
                onUpdateField={updateFormField}
                onUpdateDateRange={mergeFormFields}
                activities={
                  showActivityFilter
                    ? activities.map((activity) => ({
                        value: activity.id,
                        label: activity.name,
                      }))
                    : undefined
                }
                compact
                sectionStyle="transactions"
              />
              <DeferredFilterActions
                hasUnappliedChanges={hasUnappliedChanges}
                hasActiveFilters={hasActiveFilters}
                onApply={applyFilters}
                onReset={resetFilters}
                applyLabel={t("transactionsSection.applyFilters")}
                resetLabel={t("transactionsSection.reset")}
              />
            </div>
          ) : null}
          {walletStats ? (
            <div className="grid grid-cols-3 gap-4 border-t border-surface-200 bg-surface-50 p-4">
              <div className="text-center">
                <div className="text-sm text-surface-500">
                  {t("transactionsSection.credits")}
                </div>
                <div className="font-semibold text-success-600">
                  {formatMoney(walletStats.total_credits)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm text-surface-500">
                  {t("transactionsSection.debits")}
                </div>
                <div className="font-semibold text-error-600">
                  {formatMoney(walletStats.total_debits)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm text-surface-500">
                  {t("transactionsSection.transactionsCountLabel")}
                </div>
                <div className="font-semibold text-surface-900">
                  {walletStats.transactions_count}
                </div>
              </div>
            </div>
          ) : null}
        </>
      }
      contentClassName="px-6 py-5"
      isLoading={isLoadingTransactions}
      loadingMessage={t("transactionsSection.loadingTransactions")}
      isEmpty={!isLoadingTransactions && paginatedTransactions.length === 0}
      emptyState={
        <EmptyState
          icon={Wallet}
          title={t("transactionsSection.emptyTitle")}
          description={t("transactionsSection.emptyDescription")}
        />
      }
      pagination={
        totalPages > 1 ? (
          <Pagination
            labels={tableLabels}
            className="mt-6"
            page={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            showPageNumbers
          />
        ) : null
      }
    >
      <div className="space-y-3">
        {paginatedTransactions.map((transaction) => (
          <TransactionItem
            key={transaction.id}
            transaction={transaction}
            getBookingPath={
              entityType === "seller"
                ? (bookingId) => `/sellers/${entityId}/bookings/${bookingId}`
                : undefined
            }
          />
        ))}
      </div>
    </ListResultsCard>
  );
}
